import { BILLING_CURRENCY, DEFAULT_CURRENCY } from '../../../server/common/currency';

export const formatNumber = (value: number | string, currency: string | null | undefined) => {
  if (typeof value === 'string') {
    value = parseFloat(value);
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    // Orders created before currency capture carry none; they were all USD.
    currency: currency || DEFAULT_CURRENCY,
  });

  return formatter.format(value);
};

/** Catalog price as "$2.99" / "€2,99"; null when there is no sellable price. */
export const formatPrice = (value: number | string | null | undefined, currency: string): string | null => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!n || !Number.isFinite(n) || n <= 0) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || DEFAULT_CURRENCY,
  }).format(n);
};

/** The shop currency's symbol ("$", "€", "£") for bare inputs/labels. */
export const currencySymbol = (currency: string): string => {
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || DEFAULT_CURRENCY,
  }).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? currency;
};

export const formatBillingAmount = (price = 0): string => {
  const digits = Number.isInteger(price) ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: BILLING_CURRENCY,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(price);
};

/** Seconds → "m:ss". Returns null for missing/invalid durations. */
export const formatDuration = (seconds?: number | null): string | null => {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** Bytes → "1.2 MB" / "340 KB" / "12 B". Empty string for non-finite input. */
export const formatSize = (bytes: number): string => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
};

export const formatCompact = (value: number | string): string => {
  if (typeof value === 'string') {
    value = parseFloat(value);
  }
  if (!Number.isFinite(value)) {
    return '0';
  }
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};
